import { createSlice, current } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { v4 as uuidv4 } from 'uuid';
import SYSTEM, { SystemType } from '../constants/judgingSystems';
import type { SAjsCriterion } from '../logic/sajsCriteria';
import { SubSystemType } from '../constants/subSystemTypes';
import { SheetType } from '../constants/sheetStatus';
import TASK, { TaskType } from '../constants/taskStatus';
import { SocketType } from '../constants/socketTypes';
import { createAppAsyncThunk } from '../app/hooks';
import { StyleType } from '../constants/style';
import { AdjudicatorType } from '../constants/adjudicatorType';
import { WdsfApiParticipantStatusType } from '../constants/wdsfApiParticipantStatus';
import { HeatType } from '../constants/heatType';
import { WarningType } from '../constants/warningType';

export interface SocketElement {
  action: string;
  args: Array<any>;
  room?: string | null;
  callback?: string | null;
  namespace?: string | null;
  acknowledge: boolean;
  volatile: boolean;
  uuid?: string;
  socketId?: string;
}
export interface CurrentScreenSocket {
  adjudicator: string;
  taskUuid: string;
  category: string;
  round: string;
  dance: Dance;
  participant: TaskParticipant | null;
}
export interface CategoryShort {
  shortName: string;
  style: StyleType | string;
}
export interface Task {
  uuid: string;
  type: SocketType;
  roundUuid: string;
  adjudicatorUuid: string;
  ttItemUuid: string | null;
  judgingSystem: SystemType;
  participants: TaskParticipant[];
  dances: Dance[];
  heats: Heat[];
  sheet: SheetType;
  status: string;
  taskResults: TaskResults;
  round: RoundShort;
  category: CategoryShort;
  style?: string; // Style from ttItem
  joinedWith?: string[]; // UUIDs of other ttItems in the join group
  isJoinedTo?: string; // UUID of parent ttItem if this is a child
  assignedComponent?: string; // EL-AJS component assigned to this adjudicator
}
export interface TaskParticipant {
  uuid: string;
  entryUuid: string;
  startNo: number;
}
export interface ParticipantResult {
  uuid: string;
  roundUuid: string;
  danceUuid: string;
  judgingSystem: SystemType;
  result: any;
}
export interface ParticipantWarning {
  participantUuid: string;
  roundUuid: string;
  danceUuid: string;
  adjudicatorUuid: string;
  warning: string;
  type: WarningType;
  uuid: string;
}
export interface TaskResults {
  uuid: string;
  roundUuid: string;
  ttItemUuid: string | null;
  judgingSystem: SystemType;
  participantResults: ParticipantResult[];
  participantWarnings?: ParticipantWarning[];
  status: TaskType;
  adjudicatorUuid: string;
}
export interface ManagerItem {
  uuid: string;
  competitionUuid: string;
  roundUuid: string;
  active: boolean;
  nextActive: boolean;
  index: number;
  paused: boolean;
  name: string;
  ttItemUuid?: string | null;
  hall?: number; // Hall number this task belongs to
}
// export interface RoundParticipant {
//   uuid: string;
//   entryUuid: string;
//   roundUuid: string;
//   startNo: number;
//   started: boolean;
//   starsLeft: number;
//   starParticipant: boolean;
//   notStartedReason: null | 'excused' | 'no_show';
//   notFinished: boolean;
//   notFinishedReason: null | 'disqualified' | 'resigned' | 'health';
//   name: string;
// }
export interface RoundParticipant {
  uuid: string;
  entryUuid: string;
  roundUuid: string;
  startNo: number;
  started: boolean;
  starsLeft: number;
  starParticipant: boolean;
  notStartedReason: null | WdsfApiParticipantStatusType;
  notFinished: boolean;
  notFinishedReason: null | 'disqualified' | 'resigned' | 'health';
  finishedInRound: boolean;
  finishedPlace: number | null;
  finalRank?: number | null;
  name: string;
  details: EntryDetailsType;
  rls?: string | null;
  teamId?: string | null;
  personId?: string | null;
  wdsfApiId?: number | null;
}
export interface EntryDetailsType {
  city: string;
  club: string;
  country: string;
  coupleAge: string;
  entryName: string;
  man: string;
  woman: string;
  wdsf?: EntryDetailsWdsfType;
  pzst?: EntryDetailsPzstType;
  person1?: EntryDetailsPersonType;
  person2?: EntryDetailsPersonType;
  athletes?: EntryDetailsAthletesType[];
  additionalInfo?: string; // Additional info from PZST API (class change, etc.) or manually entered
  idNMB?: string; // optional National Member Body ID
  customFlag?: string; // optional custom flag designator (e.g. "ain-by") that overrides country-based flag image
}
export interface EntryDetailsPzstType {
  pzstId: string | null;
}
export interface EntryDetailsWdsfType {
  rls: string;
  teamId: string;
}
export interface EntryDetailsPersonType {
  firstName: string;
  lastName: string;
  min: string;
  birthYear: string;
}
export interface EntryDetailsAthletesType {
  name: string;
  surname: string;
  yearOfBirth: string;
  id: string;
}
export interface RoundParticipantShort {
  uuid: string;
  entryUuid: string;
  roundUuid: string;
}
export interface HeatParticipant {
  uuid: string;
  entryUuid: string;
  roundUuid: string;
  startNo: number;
  name?: string;
}
export interface Round {
  categoryUuid: string;
  dances: Dance[];
  expectedNumber: number;
  groupDanceTime: number;
  heatType: HeatType;
  heatsNumber: number;
  heats: Heat[];
  maxInHeat: number;
  index: number;
  isFinal: boolean;
  isPreliminary: boolean;
  isRedance: boolean;
  judgingSystem: SystemType;
  markedCouples: number;
  number: number;
  redance: boolean;
  roundName: string;
  soloDanceTime: number;
  stars: number;
  state: string;
  uuid: string;
  roundParticipants: Array<RoundParticipant>;
  judgedCriteria: number;
  status?: {
    resultsConfirmed: boolean;
    resultsPrinted: boolean;
    diplomasPrinted: boolean;
    roundClosed: boolean;
  };
}
export interface RoundShort {
  dances: Dance[];
  heats: number;
  isFinal: boolean;
  judgingSystem: SystemType;
  systemSubtype?: SubSystemType;
  markedCouples: number;
  roundName: string;
  judgedCriteria: number;
  minScoreGuidance?: number;
  maxScoreGuidance?: number;
  sajsCriteria?: SAjsCriterion[];
}
export interface Heat {
  uuid: string;
  roundUuid: string;
  danceUuid: string;
  participants: HeatParticipant[];
  solo: boolean;
  heatNo: number;
  danceHeatNo: number;
}
export interface Dance {
  desig: string;
  name: string;
  index: number;
  roundIndex: number;
  solo: boolean;
  style: StyleType;
  uuid: string;
  skipped?: boolean;
}
export interface JudgesListSettings {
  pinLoginEnabled?: boolean;
}
export interface JudgesListPayload {
  list: object;
  settings: JudgesListSettings;
}
export interface WorkState {
  timetable: object;
  judgeLetter: string;
  judgeName: string;
  judgeUuid: string;
  currentTask: string | null;
  nextTask: string | null;
  taskManagerQueue: Array<ManagerItem>;
  tasks: Array<Task>;
  tasksCompleted: Array<Task>;
  tasksResults: Array<TaskResults>;
  participantResults: any;
  judgesList: object;
  judgesListSettings: JudgesListSettings;
  socketsCache: Array<SocketElement>;
  socketsSent: Array<SocketElement>;
  lastCompletedTtUuid: string | null;
}
export interface Adjudicator {
  id: string | number;
  min: string | number;
  firstName: string;
  lastName: string;
  country: string;
  city: string;
  type: AdjudicatorType;
  subtype: any;
  classes: any;
  letter: string;
  idTs: number;
  uuid: string;
  wdsfApiId?: number | null;
}
const initialState: WorkState = {
  timetable: {},
  judgeLetter: '',
  judgeName: '',
  judgeUuid: '',
  currentTask: '',
  nextTask: '',
  tasks: [],
  tasksCompleted: [],
  tasksResults: [],
  participantResults: [],
  judgesList: {},
  judgesListSettings: { pinLoginEnabled: false },
  socketsCache: [],
  socketsSent: [],
  taskManagerQueue: [],
  lastCompletedTtUuid: null
};

export interface ServerToClientEvents {
  noArg: () => void;
  basicEmit: (a: number, b: string, c: Buffer) => void;
  withAck: (d: string, callback: (e: number) => void) => void;
}

export interface ClientToServerEvents {
  adjudicatorRoundResults: (arg0: TaskResults) => void;
}

export interface InterServerEvents {
  ping: () => void;
}

export interface SocketData {
  name: string;
  age: number;
}

const workSlice = createSlice({
  name: 'work',
  initialState,
  reducers: {
    updateTimetable(state, action: PayloadAction<object>) {
      state.timetable = action.payload;
    },

    updateJudgeLetter(state, action: PayloadAction<string>) {
      state.judgeLetter = action.payload;
    },
    updateJudgeName(state, action: PayloadAction<string>) {
      state.judgeName = action.payload;
    },
    updateJudgeUuid(state, action: PayloadAction<string>) {
      state.judgeUuid = action.payload;
    },
    updateJudgesList(state, action: PayloadAction<JudgesListPayload>) {
      state.judgesList = action.payload.list;
      if (action.payload.settings) {
        state.judgesListSettings = action.payload.settings;
      }
    },
    updateTaskManagerQueue(state, action: PayloadAction<ManagerItem[]>) {
      state.taskManagerQueue = action.payload;
      // currentTask and nextTask are derived by selectors using hall filtering
      // The reducer cannot access settings.selectedHall, so we don't set them here
    },
    updateCurrentTask(
      state,
      action: PayloadAction<ManagerItem | string | null>
    ) {
      if (action.payload === null) {
        state.currentTask = '';
      } else if (typeof action.payload === 'string') {
        state.currentTask = action.payload;
      } else {
        state.currentTask = action.payload.roundUuid;
      }
    },
    updateNextTask(state, action: PayloadAction<ManagerItem | string | null>) {
      if (action.payload === null) {
        state.nextTask = '';
      } else if (typeof action.payload === 'string') {
        state.nextTask = action.payload;
      } else {
        state.nextTask = action.payload.roundUuid;
      }
    },
    updateTask(state, action: PayloadAction<Task>) {
      const task = action.payload;
      const index = state.tasks.findIndex(
        (t: Task) =>
          t.roundUuid === task.roundUuid &&
          t.adjudicatorUuid === task.adjudicatorUuid
      );

      if (index === -1) {
        // If no matching task is found, add the new task
        state.tasks.push(task);
      } else {
        // If a matching task is found, update the existing task
        state.tasks[index] = task;
      }
    },
    
    updateTaskResults(state, action: PayloadAction<TaskResults>) {
      const taskResults = action.payload;
      const index = state.tasksResults.findIndex(
        (result: TaskResults) =>
          result.roundUuid === taskResults.roundUuid &&
          result.adjudicatorUuid === taskResults.adjudicatorUuid
      );

      if (index === -1) {
        // If no matching result is found, add the new task results
        state.tasksResults.push(taskResults);
      } else {
        // If a matching result is found, update the existing task results
        state.tasksResults[index] = taskResults;
      }
    },
    updateParticipantResults(
      state,
      action: PayloadAction<ParticipantResult[]>
    ) {
      const currentAdjudicator = state.judgeUuid;

      if (typeof state.participantResults === 'undefined') {
        state.participantResults = [];
      }
      if (typeof state.participantResults[currentAdjudicator] === 'undefined') {
        state.participantResults = {
          ...state.participantResults,
          [currentAdjudicator]: []
        };
      }
      action.payload.forEach((newPR) => {
        const search = state.participantResults[currentAdjudicator].find(
          (_: ParticipantResult) =>
            _.danceUuid === newPR.danceUuid &&
            _.uuid === newPR.uuid &&
            _.roundUuid === newPR.roundUuid
        );

        if (typeof search === 'undefined') {
          state.participantResults[currentAdjudicator] =
            state.participantResults[currentAdjudicator].concat(newPR);
        } else {
          state.participantResults[currentAdjudicator] =
            state.participantResults[currentAdjudicator]
              // .filter((_: Task) => _.adjudicatorUuid !== task.adjudicatorUuid)
              .filter(
                (_: ParticipantResult) =>
                  !(
                    _.danceUuid === search.danceUuid &&
                    _.uuid === search.uuid &&
                    _.roundUuid === search.roundUuid
                  )
              )
              .concat(newPR);
        }
      });
    },
    clearParticipantResultsForRound(state, action: PayloadAction<Task>) {
      const currentAdjudicator = state.judgeUuid;
      const { dances } = action.payload;
      if (typeof state.participantResults === 'undefined') {
        state.participantResults = [];
      }
      if (typeof state.participantResults[currentAdjudicator] === 'undefined') {
        state.participantResults = {
          ...state.participantResults,
          [currentAdjudicator]: []
        };
      }
      let tmp = state.participantResults[currentAdjudicator];
      dances.forEach((d) => {
        tmp = tmp.filter(
          (_: ParticipantResult) =>
            !(
              _.roundUuid === action.payload.roundUuid && _.danceUuid === d.uuid
            )
        );
      });
      state.participantResults[currentAdjudicator] = tmp;

    },
    clearData(state) {
      // console.log(current(state));
      state.timetable = {};
      state.judgeLetter = '';
      state.judgeName = '';
      state.judgeUuid = '';
      state.currentTask = '';
      state.nextTask = '';
      state.tasks = [];
      state.tasksCompleted = [];
      state.tasksResults = [];
      state.taskManagerQueue = [];
      state.participantResults = [];
      state.judgesList = {};
      state.socketsCache = [];
      state.socketsSent = [];
      state.taskManagerQueue = [];
      state.lastCompletedTtUuid = null;
    },
    removeTask(state, action: PayloadAction<Task>) {
      const task = action.payload;
      const search = state.tasks.find(
        (_: Task) =>
          _.roundUuid === action.payload.roundUuid &&
          _.adjudicatorUuid === task.adjudicatorUuid
      );
      if (search !== undefined) {
        state.tasks = state.tasks.filter(
          (_) =>
            !(
              _.roundUuid === search.roundUuid &&
              _.adjudicatorUuid === task.adjudicatorUuid
            )
        );
      }
    },
    updateTaskCompleted(state, action: PayloadAction<Task>) {
      const task = action.payload;
      const search = state.tasksCompleted.find(
        (_: Task) =>
          _.roundUuid === action.payload.roundUuid &&
          _.adjudicatorUuid === task.adjudicatorUuid
      );
      if (typeof search === 'undefined') {
        state.tasksCompleted = state.tasksCompleted.concat(task);
      } else {
        state.tasksCompleted = state.tasksCompleted
          .filter(
            (_: Task) =>
              _.roundUuid !== search.roundUuid &&
              _.adjudicatorUuid === task.adjudicatorUuid
          )
          .concat(task);
      }
    },
    removeTaskCompleted(state, action: PayloadAction<Task>) {
      const task = action.payload;

      const newTasksCompleted = state.tasksCompleted.filter(
        (_) =>
          _.roundUuid !== task.roundUuid ||
          _.adjudicatorUuid !== task.adjudicatorUuid
      );
      state.tasksCompleted = newTasksCompleted;
    },
    removeTaskCompletedByRound(state, action: PayloadAction<{ roundUuid: string; adjudicatorUuid: string }>) {
      const { roundUuid, adjudicatorUuid } = action.payload;
      state.tasksCompleted = state.tasksCompleted.filter(
        (_) =>
          _.roundUuid !== roundUuid ||
          _.adjudicatorUuid !== adjudicatorUuid
      );
    },
    addSocketToCache(state, action: PayloadAction<SocketElement>) {
      const newSocket = Object.assign({}, action.payload, {
        uuid: uuidv4()
      });
      state.socketsCache.push(newSocket);
    },
    removeSocketFromCache(state, action: PayloadAction<SocketElement>) {
      const newCache = state.socketsCache.filter((socket) => {
        socket?.uuid !== action.payload.uuid;
      });
      state.socketsCache = newCache;
    },
    addSocketToSent(state, action: PayloadAction<SocketElement>) {
      if (action.payload.uuid !== undefined && action.payload.uuid !== null) {
        state.socketsSent.push(action.payload);
      } else {
        const newSocket = Object.assign({}, action.payload, {
          uuid: uuidv4()
        });
        state.socketsSent.push(newSocket);
      }
    },
    removeSocketFromSent(state, action: PayloadAction<SocketElement>) {
      const newSent = state.socketsSent.filter((socket) => {
        socket?.uuid !== action.payload.uuid;
      });
      state.socketsSent = newSent;
    },
    addParticipantWarning(state, action: PayloadAction<ParticipantWarning>) {
      const warning = action.payload;
      const currentTaskResults = state.tasksResults.find(
        (result: TaskResults) =>
          result.roundUuid === warning.roundUuid &&
          result.adjudicatorUuid === warning.adjudicatorUuid
      );

      if (currentTaskResults) {
        if (!currentTaskResults.participantWarnings) {
          currentTaskResults.participantWarnings = [];
        }
        currentTaskResults.participantWarnings.push(warning);
      }
    },
    removeParticipantWarning(
      state, 
      action: PayloadAction<{ roundUuid: string; danceUuid: string; adjudicatorUuid: string; type: WarningType }>
    ) {
      const { roundUuid, danceUuid, adjudicatorUuid, type } = action.payload;
      const currentTaskResults = state.tasksResults.find(
        (result: TaskResults) =>
          result.roundUuid === roundUuid &&
          result.adjudicatorUuid === adjudicatorUuid
      );

      if (currentTaskResults?.participantWarnings) {
        currentTaskResults.participantWarnings = currentTaskResults.participantWarnings.filter(
          (warning) => !(
            warning.roundUuid === roundUuid && 
            warning.danceUuid === danceUuid && 
            warning.type === type
          )
        );
      }
    },
    updateLastCompletedTtUuid(state, action: PayloadAction<string | null>) {
      state.lastCompletedTtUuid = action.payload;
    }
  }
});

export const addEmptyTaskResults = createAppAsyncThunk(
  'work/addRoundTasksFromRound',
  async function (data: null, thunkApi) {
    const { getState, dispatch } = thunkApi;
    const currentTaskUuid = getState().work.currentTask;
    const adjudicatorUuid = getState().work.judgeUuid;
    const currentTask = getState().work.tasks.find(
      (_: Task) =>
        _.roundUuid === currentTaskUuid && _.adjudicatorUuid === adjudicatorUuid
    );
    if (currentTaskUuid !== null && currentTask !== undefined) {
      // console.log(currentTaskUuid);
      // console.log(getState().work.tasks);
      const participantResults: ParticipantResult[] = [];
      currentTask?.dances.forEach((dance) => {
        currentTask.participants.forEach((participant) => {
          const newResult =
            currentTask.judgingSystem === SYSTEM.MARKS ? 0 : null;
          const newParticipantResult: ParticipantResult = {
            uuid: participant.uuid,
            roundUuid: currentTask.roundUuid,
            danceUuid: dance.uuid,
            result: newResult,
            judgingSystem: currentTask.judgingSystem
          };
          participantResults.push(newParticipantResult);
        });
      });
      const newTaskResults: TaskResults = {
        uuid: uuidv4(),
        roundUuid: currentTask.roundUuid,
        ttItemUuid: currentTask.ttItemUuid,
        judgingSystem: currentTask.judgingSystem,
        participantResults: [],
        status: TASK.IN_PROGRESS,
        adjudicatorUuid: adjudicatorUuid
      };
      dispatch(updateTaskResults(newTaskResults));
      dispatch(updateParticipantResults(participantResults));
    }
  }
);
export const updateParticipantResultsAndPrepareCurrent = createAppAsyncThunk(
  'work/updateParticipantResultsAndPrepareCurrent',
  async function (newParticipantResult: ParticipantResult, thunkApi) {
    const { dispatch } = thunkApi;
    // await Promise.all([
    //   dispatch(updateParticipantResults([newParticipantResult]))
    // ]).then(() => prepareCurrentRoundResults(TASK.IN_PROGRESS));
    dispatch(updateParticipantResults([newParticipantResult]));
    // prepareCurrentRoundResults(TASK.IN_PROGRESS);
  }
);
export const prepareCurrentRoundResults = createAppAsyncThunk(
  'work/prepareCurrentRoundResults',
  async function (status: TaskType, thunkApi) {
    const { getState, dispatch } = thunkApi;

    let newStatus: TaskType = TASK.IN_PROGRESS;
    if (status !== null) {
      newStatus = status;
    }
    const currentTaskUuid = getState().work.currentTask;
    const adjudicatorUuid = getState().work.judgeUuid;

    const currentTask = getState().work.tasks.find(
      (_: Task) =>
        _.roundUuid === currentTaskUuid && _.adjudicatorUuid === adjudicatorUuid
    );

    // console.log('foundTask', currentTask);
    if (currentTask !== undefined) {
      const currentFetchedResult = getState().work.tasksResults.find(
        (_) =>
          _.roundUuid === currentTask?.roundUuid &&
          _.adjudicatorUuid === adjudicatorUuid
        // &&
        // _.ttItemUuid === currentTask?.ttItemUuid
      );
      let currentResult = currentFetchedResult;
      if (currentFetchedResult === undefined) {
        const newTaskResults: TaskResults = {
          uuid: uuidv4(),
          roundUuid: currentTask.roundUuid,
          ttItemUuid: currentTask.ttItemUuid,
          judgingSystem: currentTask.judgingSystem,
          participantResults: [],
          status: newStatus,
          adjudicatorUuid: adjudicatorUuid
        };
        currentResult = newTaskResults;
      }
      if (currentTaskUuid !== null && currentResult !== undefined) {
        const participantResults = getState().work.participantResults[
          adjudicatorUuid
        ].filter(
          (_: ParticipantResult) => _.roundUuid === currentTask.roundUuid
        );
        const newResults = Object.assign({}, currentResult, {
          participantResults: participantResults
        });
        // console.log('pr', participantResults);
        // console.log('nr', newResults.participantResults);

        dispatch(updateTaskResults(newResults));
        const newTask: Task = Object.assign({}, currentTask, {
          status: newStatus,
          taskResults: newResults
        });
        if (newStatus === TASK.DONE) {
          const taskManagerQueue = getState().work.taskManagerQueue;

          const index = taskManagerQueue.findIndex(
            (_) => _.roundUuid === newTask.roundUuid
          );
          
          // Save the ttUuid of the completed task
          if (newTask.ttItemUuid) {
            dispatch(updateLastCompletedTtUuid(newTask.ttItemUuid));
          }
          
          if (taskManagerQueue[index + 1] !== undefined) {
            dispatch(updateCurrentTask(taskManagerQueue[index + 1]));
          } else {
            dispatch(updateCurrentTask(null));
          }

          dispatch(updateTask(newTask));
          dispatch(updateTaskCompleted(newTask));
          dispatch(removeTask(newTask));
        } else {
          dispatch(updateTask(newTask));
        }
      }
    }
  }
);

/**
 * Prepare results for all joined tasks
 */
export const prepareJoinedTasksResults = createAppAsyncThunk(
  'work/prepareJoinedTasksResults',
  async function (status: TaskType, thunkApi) {
    const { getState, dispatch } = thunkApi;

    const joinedTasks = selectJoinedTasks(getState());
    const adjudicatorUuid = getState().work.judgeUuid;
    
    if (!joinedTasks || joinedTasks.length === 0 || !adjudicatorUuid) {
      return;
    }

    let newStatus: TaskType = TASK.IN_PROGRESS;
    if (status !== null) {
      newStatus = status;
    }

    // Process each joined task
    for (const task of joinedTasks) {
      const existingResult = getState().work.tasksResults.find(
        (_) =>
          _.roundUuid === task.roundUuid &&
          _.adjudicatorUuid === adjudicatorUuid
      );
      
      const currentResult: TaskResults = existingResult || {
        uuid: uuidv4(),
        roundUuid: task.roundUuid,
        ttItemUuid: task.ttItemUuid,
        judgingSystem: task.judgingSystem,
        participantResults: [],
        status: newStatus,
        adjudicatorUuid: adjudicatorUuid
      };

      // Get participant results for this specific task's round
      const participantResults = getState().work.participantResults[
        adjudicatorUuid
      ]?.filter(
        (_: ParticipantResult) => _.roundUuid === task.roundUuid
      ) || [];

      const newResults: TaskResults = {
        uuid: currentResult.uuid,
        roundUuid: currentResult.roundUuid,
        ttItemUuid: currentResult.ttItemUuid,
        judgingSystem: currentResult.judgingSystem,
        participantResults: participantResults,
        participantWarnings: currentResult.participantWarnings,
        status: newStatus,
        adjudicatorUuid: currentResult.adjudicatorUuid
      };

      dispatch(updateTaskResults(newResults));
      
      const updatedTask: Task = {
        ...task,
        status: newStatus,
        taskResults: newResults
      };

      if (newStatus === TASK.DONE) {
        if (task.ttItemUuid) {
          dispatch(updateLastCompletedTtUuid(task.ttItemUuid));
        }
        dispatch(updateTask(updatedTask));
        dispatch(updateTaskCompleted(updatedTask));
        dispatch(removeTask(updatedTask));
      } else {
        dispatch(updateTask(updatedTask));
      }
    }
    
    // Handle navigation for DONE status
    if (newStatus === TASK.DONE) {
      const taskManagerQueue = getState().work.taskManagerQueue;
      const currentTask = selectCurrentTask(getState());
      
      if (currentTask) {
        const index = taskManagerQueue.findIndex(
          (_) => _.roundUuid === currentTask.roundUuid
        );
        
        if (taskManagerQueue[index + 1] !== undefined) {
          dispatch(updateCurrentTask(taskManagerQueue[index + 1]));
        } else {
          dispatch(updateCurrentTask(null));
        }
      }
    }
  }
);

// Export actions
export const {
  updateTimetable,
  updateJudgeLetter,
  updateJudgeName,
  updateJudgesList,
  updateCurrentTask,
  updateNextTask,
  updateJudgeUuid,
  updateTaskManagerQueue,
  updateTask,
  updateTaskResults,
  updateParticipantResults,
  clearParticipantResultsForRound,
  removeTask,
  clearData,
  updateTaskCompleted,
  removeTaskCompleted,
  removeTaskCompletedByRound,
  addSocketToCache,
  addSocketToSent,
  removeSocketFromCache,
  removeSocketFromSent,
  addParticipantWarning,
  removeParticipantWarning,
  updateLastCompletedTtUuid,
} = workSlice.actions;

/**
 * Start a local training session: wipe existing work state, install a synthetic
 * adjudicator, push the synthesized training task and select it as current.
 * No socket traffic is produced — see `selectIsTrainingMode` gating in screens.
 */
export const startTrainingSession = createAppAsyncThunk(
  'work/startTrainingSession',
  async function (
    payload: { task: Task; judge: { uuid: string; letter: string; name: string } },
    thunkApi
  ) {
    const { dispatch } = thunkApi;
    dispatch(clearData());
    dispatch(updateJudgeLetter(payload.judge.letter));
    dispatch(updateJudgeName(payload.judge.name));
    dispatch(updateJudgeUuid(payload.judge.uuid));
    dispatch(updateTask(payload.task));
    dispatch(updateCurrentTask(payload.task.roundUuid));
  }
);

//selectors
export const selectTimetable = (state: RootState) => {
  return state.work.timetable;
};
export const selectJudge = (state: RootState) => {
  return {
    letter: state.work.judgeLetter,
    name: state.work.judgeName,
    uuid: state.work.judgeUuid
  };
};
export const selectJudgesList = (state: RootState) => {
  return state.work.judgesList;
};
export const selectJudgesListSettings = (state: RootState) => {
  return state.work.judgesListSettings;
};
export const selectPinLoginEnabled = (state: RootState) => {
  return state.work.judgesListSettings?.pinLoginEnabled || false;
};
export const selectCurrentTaskUuid = (state: RootState) => {
  return state.work.currentTask;
};
export const selectNextTaskUuid = (state: RootState) => {
  return state.work.nextTask;
};
export const selectCurrentTask = (state: RootState) => {
  const currentAdjudicator = state.work.judgeUuid;
  const selectedHall = state.settings.selectedHall;

  // 1. If user explicitly set a current task and it has a matching Task object, use it
  const currentTaskUuid = state.work.currentTask;
  if (currentTaskUuid !== null && currentTaskUuid !== '') {
    const explicitTask = state.work.tasks.find(
      (_: Task) =>
        _.roundUuid === currentTaskUuid &&
        _.adjudicatorUuid === currentAdjudicator
    );
    if (explicitTask) return explicitTask;
  }

  // 2. Fall back: find active item in the hall-filtered queue
  const queue = state.work.taskManagerQueue;
  const hallFiltered = (!selectedHall || selectedHall === 'all')
    ? queue
    : queue.filter((item: ManagerItem) => (item.hall || 1) === selectedHall);

  const activeItem = hallFiltered.find((item: ManagerItem) => item.active === true);
  if (activeItem && currentAdjudicator) {
    return state.work.tasks.find(
      (_: Task) =>
        _.roundUuid === activeItem.roundUuid &&
        _.adjudicatorUuid === currentAdjudicator
    ) || null;
  }

  return null;
};

/**
 * Get all tasks for joined timetable items
 * Returns array of tasks for the same adjudicator in the join group
 */
export const selectJoinedTasks = (state: RootState) => {
  const currentTask = selectCurrentTask(state);
  const currentAdjudicator = state.work.judgeUuid;
  
  if (!currentTask || !currentTask.ttItemUuid) {
    return [];
  }
  
  // Get all ttItem UUIDs in the join group
  const joinGroupUuids: string[] = [];
  
  // Find the parent task (either current task or the one it's joined to)
  let parentTask: Task | undefined;
  let parentUuid: string;
  
  if (currentTask.isJoinedTo) {
    // Current task is a child, find the parent
    parentUuid = currentTask.isJoinedTo;
    parentTask = state.work.tasks.find(
      (t: Task) => t.ttItemUuid === parentUuid && t.adjudicatorUuid === currentAdjudicator
    );
  } else {
    // Current task is the parent or standalone
    parentUuid = currentTask.ttItemUuid;
    parentTask = currentTask;
  }
  
  // Add parent UUID
  joinGroupUuids.push(parentUuid);
  
  // Add all children UUIDs from parent's joinedWith array
  if (parentTask?.joinedWith && parentTask.joinedWith.length > 0) {
    joinGroupUuids.push(...parentTask.joinedWith);
  }

  // Get all tasks for these ttItems with same adjudicator
  // Note: Don't filter by roundUuid because joined items can have different rounds/categories
  const joinedTasks = state.work.tasks.filter(
    (task: Task) =>
      task.adjudicatorUuid === currentAdjudicator &&
      task.ttItemUuid &&
      joinGroupUuids.includes(task.ttItemUuid)
  );
  // Sort by ttItemUuid to ensure consistent order (parent first, then children)
  return joinedTasks.sort((a, b) => {
    if (a.isJoinedTo && !b.isJoinedTo) return 1;
    if (!a.isJoinedTo && b.isJoinedTo) return -1;
    return (a.ttItemUuid || '').localeCompare(b.ttItemUuid || '');
  });
};
export const selectNextTask = (state: RootState) => {
  const currentAdjudicator = state.work.judgeUuid;
  const selectedHall = state.settings.selectedHall;

  // Filter queue by hall
  const queue = state.work.taskManagerQueue;
  const hallFiltered = (!selectedHall || selectedHall === 'all')
    ? queue
    : queue.filter((item: ManagerItem) => (item.hall || 1) === selectedHall);

  // 1. Find nextActive item in the hall-filtered queue
  const nextActiveItem = hallFiltered.find(
    (item: ManagerItem) => item.nextActive === true
  );
  if (nextActiveItem && currentAdjudicator) {
    const task = state.work.tasks.find(
      (_: Task) =>
        _.roundUuid === nextActiveItem.roundUuid &&
        _.adjudicatorUuid === currentAdjudicator
    );
    if (task) return task;
  }

  // 2. Fallback: try state.work.nextTask
  const nextTaskUuid = state.work.nextTask;
  if (nextTaskUuid !== null && nextTaskUuid !== '') {
    const task = state.work.tasks.find(
      (_: Task) =>
        _.roundUuid === nextTaskUuid &&
        _.adjudicatorUuid === currentAdjudicator
    );
    if (task) return task;
  }

  return null;
};
export const selectTaskManagerQueue = (state: RootState) => {
  return state.work.taskManagerQueue;
};
/**
 * Get task manager queue filtered by selected hall.
 * If selectedHall is 'all', returns all items.
 */
export const selectTaskManagerQueueForHall = (state: RootState) => {
  const selectedHall = state.settings.selectedHall;
  if (!selectedHall || selectedHall === 'all') {
    return state.work.taskManagerQueue;
  }
  return state.work.taskManagerQueue.filter(
    (item: ManagerItem) => (item.hall || 1) === selectedHall,
  );
};
/**
 * Get all active tasks (from any hall).
 * Adjudicators may judge in multiple halls simultaneously.
 */
export const selectActiveTasksForHall = (state: RootState): ManagerItem[] => {
  const allTasks = state.work.taskManagerQueue;
  const activeTasks = allTasks.filter(
    (item: ManagerItem) => item.active === true,
  );
  const selectedHall = state.settings.selectedHall;
  return activeTasks;
};
/**
 * Get the active task for the selected hall (single, for backward compatibility).
 */
export const selectActiveTaskForHall = (state: RootState) => {
  const selectedHall = state.settings.selectedHall;
  const activeTasks = selectActiveTasksForHall(state);
  if (!selectedHall || selectedHall === 'all') {
    return activeTasks[0] || null;
  }
  return activeTasks.find((item: ManagerItem) => (item.hall || 1) === selectedHall) || activeTasks[0] || null;
};
/**
 * Get the next active task for the selected hall.
 */
export const selectNextActiveTaskForHall = (state: RootState) => {
  const queue = selectTaskManagerQueueForHall(state);
  return queue.find((item: ManagerItem) => item.nextActive === true) || null;
};
export const selectActiveTask = (state: RootState) => {
  return state.work.taskManagerQueue.find((item: ManagerItem) => item.active);
};
export const selectTasks = (state: RootState) => {
  return state.work.tasks;
};

export const selectAllTaskResults = (state: RootState) => {
  const searchRes = state.work.tasksResults;
  return searchRes === undefined ? null : searchRes;
};

export const selectCurrentTaskResults = (state: RootState) => {
  const currentTask = selectCurrentTask(state);
  const currentAdjudicator = state.work.judgeUuid;
  // console.log('search');
  // console.log(state.work.tasksResults);
  const searchRes = state.work.tasksResults?.find(
    (item: TaskResults) =>
      item.roundUuid === currentTask?.roundUuid &&
      item.adjudicatorUuid === currentAdjudicator
  );
  return searchRes === undefined ? null : searchRes;
};

export const selectTaskResultsByRoundUuid = (roundUuid: string) => (state: RootState) => {
  const currentAdjudicator = state.work.judgeUuid;
  const searchRes = state.work.tasksResults?.find(
    (item: TaskResults) =>
      item.roundUuid === roundUuid &&
      item.adjudicatorUuid === currentAdjudicator
  );
  return searchRes === undefined ? null : searchRes;
};

/**
 * Get all task results for joined tasks
 */
export const selectJoinedTasksResults = (state: RootState): TaskResults[] => {
  const joinedTasks = selectJoinedTasks(state);
  const currentAdjudicator = state.work.judgeUuid;
  
  if (!joinedTasks || joinedTasks.length === 0) {
    return [];
  }
  
  return joinedTasks
    .map((task) => {
      const results = state.work.tasksResults?.find(
        (item: TaskResults) =>
          item.roundUuid === task.roundUuid &&
          item.adjudicatorUuid === currentAdjudicator
      );
      return results;
    })
    .filter((result): result is TaskResults => result !== undefined);
};
export const selectCurrentTaskParticipantResults = (state: RootState) => {
  const currentTask = selectCurrentTask(state);
  const adjudicatorUuid = state.work.judgeUuid;
  if (state.work.participantResults[adjudicatorUuid] !== undefined) {
    const searchRes = state.work.participantResults[adjudicatorUuid].filter(
      (item: ParticipantResult) => item.roundUuid === currentTask?.roundUuid
    );
    return searchRes === undefined ? [] : searchRes;
  } else {
    return [];
  }
};
export const selectTasksCompleted = (state: RootState) => {
  return state.work.tasksCompleted;
};
export const selectSocketsCache = (state: RootState) => {
  return state.work.socketsCache;
};
export const selectSocketsSent = (state: RootState) => {
  return state.work?.socketsSent;
};
export const selectLastCompletedTtUuid = (state: RootState) => {
  return state.work.lastCompletedTtUuid;
};
// Export reducer
export default workSlice.reducer;
